import {
  Box,
  Slide,
  Stack,
  type Theme,
  Toolbar,
  styled,
  useMediaQuery,
  useScrollTrigger,
} from "@mui/material"
import { type ReactNode, useRef } from "react"
import { Link } from "react-router-dom"
import { AppBar } from "../AppBar"
import { MINI_HEADER_ID, SUB_HEADER_PORTAL_ID } from "./stickyHeader.const"

const TOOLBAR_HEIGHT = "99px"
const HEADER_NAV_HEIGHT = 40

export interface RenderPartConf {
  isFixed: boolean
  isCompact: boolean
}

interface AppHeaderProps {
  renderNav: (conf: RenderPartConf & { height: number }) => ReactNode
  renderActions: (conf: RenderPartConf) => ReactNode
}

export function AppHeader({ renderActions, renderNav }: AppHeaderProps) {
  const isCompact = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"))
  const appBarRef = useRef<HTMLDivElement>(null)
  const showMiniAppBar = useScrollTrigger({
    disableHysteresis: true,
    threshold: appBarRef.current?.offsetHeight,
  })
  const Nav = renderNav({
    height: HEADER_NAV_HEIGHT,
    isCompact,
    isFixed: false,
  })

  return (
    <>
      {/* FULL (NOT FIXED/MINIFIED) */}
      <AppBarStyled
        position="static"
        elevation={0}
        ref={appBarRef}
        sx={!Nav ? { height: "auto" } : {}}
      >
        <Toolbar sx={{ height: TOOLBAR_HEIGHT, padding: "0!important" }}>
          <Stack
            direction="row"
            spacing={4}
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            {/* LOGO */}
            <Link
              to="/"
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                alt="pharm_upp"
                src="/logos/pharmUpp/logo_pharmUpp_full.svg"
                height="100%"
              />
            </Link>
            {/* ACTIONS */}
            {renderActions({ isFixed: false, isCompact })}
          </Stack>
        </Toolbar>

        {/* NAV */}
        {Nav}
      </AppBarStyled>

      {/* FIXED + MINIFIED */}
      <HeaderMini
        visible={showMiniAppBar}
        isCompact={isCompact}
        renderActions={renderActions}
        renderNav={renderNav}
      />
    </>
  )
}

const HEADER_MINI_HEIGHT = 64

const HeaderMini = ({
  visible,
  isCompact,
  renderActions,
  renderNav,
}: { visible: boolean; isCompact: boolean } & AppHeaderProps) => (
  <Slide in={visible}>
    <Box position="fixed" zIndex={10} width="100%" id={MINI_HEADER_ID}>
      <AppBarStyled
        position="relative"
        elevation={0}
        sx={{ height: `${HEADER_MINI_HEIGHT}px` }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          height="100%"
        >
          <Stack direction="row" alignItems="center" spacing={4} height="100%">
            <Link to="/" style={{ display: "flex" }}>
              <img
                alt="pharm_upp"
                src="/logos/pharmUpp/logo_pharmUpp_mini.svg"
                width="40px"
                height="40px"
              />
            </Link>
            {renderNav({ isFixed: true, isCompact, height: HEADER_MINI_HEIGHT })}
          </Stack>

          {renderActions({ isFixed: true, isCompact })}
        </Stack>
      </AppBarStyled>

      <Stack id={SUB_HEADER_PORTAL_ID} />
    </Box>
  </Slide>
)

const AppBarStyled = styled(AppBar)({
  borderWidth: 0,
})
